import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { confirmDelete } from '@functions/entity'

import useTrackingLink, { useTrackingLinkForm } from '@hooks/useTrackingLink'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditTrackingLinkModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTrackingLink: (payload: HandleCallbackActionParams) => void,
  updateTrackingLink: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditTrackingLinkModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createTrackingLink, updateTrackingLink } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const trackingLink = digObject(modalPayload, 'trackingLink', {})

  const newRecord = !trackingLink.id

  const {
    callbacks: {
      createTrackingLink: createFn,
      deleteTrackingLink: deleteFn,
      updateTrackingLink: updateFn,
    },
    creating,
    deleting,
    updating,
  } = useTrackingLink(trackingLink)

  const {
    entityState,
    entityState: {
      title,
      url,
    },
    errors,
    handlers,
    saveEnabled,
  } = useTrackingLinkForm(trackingLink, { validateOn: 'change' })

  const createOrUpdateTrackingLinkPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      include_tracking_link_shortened_url: true,
      include_tracking_link_total_views: true,
    },
    toastText: `Tracking Link ${newRecord ? 'Created' : 'Updated'}`,
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    options: {
      entityParams: trackingLink,
    },
    strings: {
      entityName: 'Tracking Link',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Tracking Link"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* URL" errorMessage={errors.url} marginTop="large">
            <input name="url" type="text" value={url} {...handlers} />
          </Form.Field>

          {!newRecord && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(confirmDeletePayload)}
                size="medium"
              >
                Delete Tracking Link
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createTrackingLink(createOrUpdateTrackingLinkPayload)
              : () => updateTrackingLink(createOrUpdateTrackingLinkPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Tracking Link` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditTrackingLinkModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
